import { useEffect, useState } from "react";
import { Alert, Button, Divider, Stack } from "@mui/material";
import AppleIcon from "@mui/icons-material/Apple";
import FacebookIcon from "@mui/icons-material/Facebook";
import SvgIcon from "@mui/material/SvgIcon";
import { useTranslation } from "react-i18next";
import { fetchOAuthProviders, oauthStartUrl } from "../../api/auth";
import { markOAuthFlowStarted } from "../../utils/oauthFlowGuard";

const GoogleIcon = (props) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path d="M21.6 12.23c0-.72-.06-1.41-.18-2.07H12v3.91h5.38a4.6 4.6 0 0 1-2 3.02v2.51h3.24c1.9-1.75 2.98-4.33 2.98-7.37Z" fill="#4285F4" />
    <path d="M12 22c2.7 0 4.97-.9 6.62-2.4l-3.24-2.51c-.9.6-2.05.95-3.38.95-2.6 0-4.8-1.76-5.59-4.12H3.06v2.59A10 10 0 0 0 12 22Z" fill="#34A853" />
    <path d="M6.41 13.92A6 6 0 0 1 6.1 12c0-.67.11-1.31.31-1.92V7.49H3.06A10 10 0 0 0 2 12c0 1.61.39 3.14 1.06 4.51l3.35-2.59Z" fill="#FBBC05" />
    <path d="M12 5.96c1.47 0 2.79.51 3.83 1.5l2.87-2.87C16.96 2.97 14.7 2 12 2a10 10 0 0 0-8.94 5.49l3.35 2.59C7.2 7.72 9.4 5.96 12 5.96Z" fill="#EA4335" />
  </SvgIcon>
);

const MicrosoftIcon = (props) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path fill="#F25022" d="M3 3h8.5v8.5H3z" />
    <path fill="#7FBA00" d="M12.5 3H21v8.5h-8.5z" />
    <path fill="#00A4EF" d="M3 12.5h8.5V21H3z" />
    <path fill="#FFB900" d="M12.5 12.5H21V21h-8.5z" />
  </SvgIcon>
);

const providers = [
  { id: "google", Icon: GoogleIcon },
  { id: "facebook", Icon: FacebookIcon },
  { id: "apple", Icon: AppleIcon },
  { id: "microsoft", Icon: MicrosoftIcon },
];

const SocialSignInButtons = ({ returnTo = "/dashboard" }) => {
  const { t } = useTranslation();
  const [providerStatus, setProviderStatus] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let mounted = true;

    fetchOAuthProviders()
      .then((data) => {
        if (mounted) setProviderStatus(data.providers || {});
      })
      .catch(() => {
        if (mounted) setLoadFailed(true);
      });

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <Stack spacing={1.5}>
      <Divider>{t("auth.social.divider")}</Divider>
      {loadFailed ? (
        <Alert severity="info">{t("auth.social.unavailable")}</Alert>
      ) : null}
      {providers.map(({ id, Icon }) => {
        const configured = providerStatus?.[id] !== false;

        return (
          <Button
            key={id}
            component="a"
            href={configured ? oauthStartUrl(id, returnTo) : undefined}
            onClick={() => {
              if (configured) {
                markOAuthFlowStarted({ provider: id, returnTo });
              }
            }}
            variant="outlined"
            fullWidth
            disabled={!configured}
            startIcon={<Icon />}
            sx={{
              bgcolor: "rgba(255, 250, 240, 0.72)",
              justifyContent: "flex-start",
              px: 2,
            }}
          >
            {configured
              ? t(`auth.social.${id}`)
              : t("auth.social.providerUnavailable", {
                  provider: t(`auth.social.providerNames.${id}`),
                })}
          </Button>
        );
      })}
    </Stack>
  );
};

export default SocialSignInButtons;
